import { useEffect, createContext, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import api from "./api";
import logger from "../utils/logger";
import { Eventbus } from "../utils/index";
import { events } from "../utils/constants/index";
import ToastService from "./toast.service";

// Generic message processor for data change events
const processDataChangeMessage = (eventName, message) => {
  logger.debug(`Processing SignalR message for ${eventName}:`, message);
  try {
    // Special handling for delete events
    if (eventName.includes('.delete')) {
      logger.log(`Processing ${eventName} deletion for ID:`, message);
      Eventbus.emit(eventName, { id: Number(message) });
      return;
    }
    // For other events, parse JSON strings or emit objects directly
    if (typeof message === "string") {
      try {
        const parsed = JSON.parse(message);
        Eventbus.emit(eventName, parsed);
      } catch (e) {
        logger.warn(`Failed to parse SignalR message for ${eventName}:`, message);
      }
    } else {
      Eventbus.emit(eventName, message);
    }
  } catch (error) {
    logger.error(`Error processing SignalR ${eventName}:`, error, message);
  }
};

// Special handler for email processing status (has specific toast notifications)
const processEmailProcessingStatus = (message) => {
  const { status, timestamp, message: errorMessage } = message;

  switch (status) {
    case "completed":
      ToastService.success("Email processing completed successfully", 'email-stats');
      break;
    case "started":
      ToastService.info("Email processing started", 'email-stats');
      break;
    case "noEmails":
      ToastService.info("No emails found for processing", 'email-stats');
      break;
    case "error":
      ToastService.error(`Email processing error: ${errorMessage}`, 'email-stats');
      break;
    default:
      logger.warn(`Unknown status received: ${status} at ${timestamp}`);
  }

  // Still emit the event for any other listeners
  Eventbus.emit(events.SIGNALR.EMAIL_PROCESSING_STATUS, message);
};

export async function connectToSignalR({ connection }) {
  logger.log("Connecting to SignalR...");
  try {
    if (!connection) {
      throw new Error("No connection object provided");
    }

    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(connection.url, {
        accessTokenFactory: () => connection.accessToken,
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    // Dynamically register handlers for all SignalR events
    Object.entries(events.SIGNALR).forEach(([eventKey, eventName]) => {
      logger.log(`Registering SignalR handler for: ${eventName}`);

      if (eventName === events.SIGNALR.EMAIL_PROCESSING_STATUS) {
        // Special handling for email processing status
        hubConnection.on(eventName, processEmailProcessingStatus);
      } else {
        // Generic handling for data change events
        hubConnection.on(eventName, (message) => processDataChangeMessage(eventName, message));
      }
    });

    await hubConnection.start();
    logger.log("SignalR connection established");

    // Return the connection object in case the consumer needs to stop it or add more handlers
    return hubConnection;
  } catch (error) {
    logger.error("Error connecting to SignalR:", error);
    throw error;
  }
}

// Global connection manager to persist across component re-mounts
const connectionManager = {
  connection: null,
  isConnecting: false,
  disconnectTimer: null,

  async getConnection() {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }

    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      logger.log("Returning existing SignalR connection");
      return this.connection;
    }

    if (this.isConnecting) {
      logger.log("Connection already in progress, waiting...");
      // Wait for the current connection attempt to complete
      while (this.isConnecting) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return this.connection;
    }

    return await this.createConnection();
  },

  async createConnection() {
    this.isConnecting = true;

    try {
      if (this.connection) {
        logger.log("Cleaning up existing SignalR connection...");
        await this.connection.stop().catch((err) => logger.error("Error stopping existing connection:", err));
        this.connection = null;
      }

      logger.log("Fetching SignalR connection details...");
      const response = await api.getSignalRConnection();

      if (!response.url || !response.accessToken) {
        throw new Error("Invalid SignalR connection response");
      }

      this.connection = await connectToSignalR({ connection: response });
      logger.log("SignalR connection successfully established");

      return this.connection;
    } catch (error) {
      logger.error("Error creating SignalR connection:", error);
      ToastService.error("Error establishing SignalR connection");
      throw error;
    } finally {
      this.isConnecting = false;
    }
  },

  async disconnect() {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
    }

    this.disconnectTimer = setTimeout(async () => {
      if (this.connection) {
        logger.log("Disconnecting SignalR...");
        await this.connection.stop().catch((err) => logger.error("Error stopping SignalR connection:", err));
        this.connection = null;
      }
      this.isConnecting = false;
      this.disconnectTimer = null;
    }, 0);
  },

  cancelDisconnect() {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
  }
};

const signalRContext = createContext();

// Export for testing purposes - allows tests to reset state between runs
export const __resetConnectionManagerForTesting = () => {
  connectionManager.connection = null;
  connectionManager.isConnecting = false;
  if (connectionManager.disconnectTimer) {
    clearTimeout(connectionManager.disconnectTimer);
    connectionManager.disconnectTimer = null;
  }
};

// Export internals for testing
export { processDataChangeMessage, processEmailProcessingStatus, connectionManager };

export default function SignalRService() {
  const initializationRef = useRef(false);

  useEffect(() => {
    connectionManager.cancelDisconnect();

    if (initializationRef.current) {
      logger.log("SignalR service already initialized, skipping...");
      return;
    }

    initializationRef.current = true;
    logger.log("Initializing SignalR service...");

    // Initialize the connection
    connectionManager.getConnection().catch((error) => {
      logger.error("Failed to initialize SignalR connection:", error);
    });

    return () => {
      connectionManager.disconnect().catch((error) => {
        logger.error("Error during SignalR service cleanup:", error);
      });
      logger.log("SignalR service cleanup");
    };
  }, []);

  return (
    <signalRContext.Provider
      value={{
        connectToSignalR: () => connectionManager.getConnection(),
        disconnect: () => connectionManager.disconnect(),
      }}
    ></signalRContext.Provider>
  );
}
